import { useState, type FormEvent } from 'react';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Field, TextInput } from '../components/ui/Field';
import { PageHeader } from '../components/ui/Page';
import { usePageTitle } from '../hooks/usePageTitle';

export default function ChangePasswordPage() {
  usePageTitle('Change password');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const user = auth.currentUser;
  const canSave =
    !saving &&
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    newPassword === confirmPassword;

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user?.email || !canSave) {
      setError('You must be signed in with an email account to change your password.');
      return;
    }

    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setNotice('Password updated.');
    } catch (e) {
      console.error(e);
      setError((e as Error).message || 'Could not update password.');
    } finally {
      setSaving(false);
    }
  }

  const checks = [
    { label: 'At least 8 characters', ok: newPassword.length >= 8 },
    { label: 'A number or a symbol', ok: /[0-9\W_]/.test(newPassword) },
    { label: 'Not the password you are replacing', ok: !!newPassword && newPassword !== currentPassword },
    { label: 'Both new entries match', ok: !!newPassword && newPassword === confirmPassword },
  ];

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader title="Change password" description="This changes the password for your own account." />

      <div className="mt-5 space-y-2.5 empty:hidden">
        {error && <Alert tone="error">{error}</Alert>}
        {notice && <Alert tone="success">{notice}</Alert>}
      </div>

      <form onSubmit={save} className="mt-5 overflow-hidden rounded-[14px] border border-hair bg-elev">
        <div className="flex flex-col gap-4 p-5">
          <Field label="Current password">
            <TextInput type="password" autoComplete="current-password" value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)} />
          </Field>
          <Field label="New password">
            <TextInput type="password" autoComplete="new-password" value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)} />
          </Field>
          <Field label="Confirm new password">
            <TextInput type="password" autoComplete="new-password" value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)} />
          </Field>

          {/* A live checklist, ticking off as you type, rather than an error after
              the button has already been pressed. */}
          <ul className="flex flex-col gap-1.5 rounded-[10px] bg-surface px-3.5 py-3">
            {checks.map((check) => (
              <li key={check.label} className={`flex items-center gap-2 text-[13.5px] ${check.ok ? 'text-success-ink' : 'text-ink2'}`}>
                <span aria-hidden className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${check.ok ? 'bg-success-fill text-white' : 'bg-sunken text-ink3'}`}>
                  {check.ok ? '✓' : ''}
                </span>
                {check.label}
              </li>
            ))}
          </ul>

          {/* Firebase ends every other session when a password changes: other devices,
              including the iPhone app, are signed out within about an hour. */}
          <Alert tone="info">
            Changing your password signs you out of Checkover on your other devices, including the
            iPhone app, within about an hour. Sign in there again with the new password.
          </Alert>
        </div>

        <div className="flex justify-end border-t border-rowhair px-5 py-3.5">
          <Button type="submit" size="lg" loading={saving} disabled={!canSave}>Change password</Button>
        </div>
      </form>
    </div>
  );
}
